import { State } from "@features/base/types";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AdminBank,
  AdminBanksResponse,
  BankRegex,
  createAdminBankApi,
  CreateAdminBankRequest,
  deleteAdminBankApi,
  getAdminBanksApi,
  RegexForm,
  UpdateAdminBankApi,
  UpdateAdminBankRequest,
} from "./api";

export const getAdminBanks = createAsyncThunk<AdminBanksResponse, void>(
  "adminOrders/getAdminBanks",
  async (_, { rejectWithValue }) => {
    try {
      return await getAdminBanksApi(/* data */);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createAdminBank = createAsyncThunk<
  string,
  { data: CreateAdminBankRequest }
>("adminOrders/createAdminBank", async ({ data }, { rejectWithValue }) => {
  try {
    return await createAdminBankApi(data);
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const updateAdminBank = createAsyncThunk<
  string,
  { bankId: string; data: UpdateAdminBankRequest }
>(
  "adminOrders/updateAdminBank",
  async ({ bankId, data }, { rejectWithValue }) => {
    try {
      return await UpdateAdminBankApi(bankId, data);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteAdminBank = createAsyncThunk<string, { bankId: string }>(
  "adminOrders/deleteAdminBank",
  async ({ bankId }, { rejectWithValue }) => {
    try {
      return await deleteAdminBankApi(bankId);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

type AdminBanksState = State<{
  banks: AdminBank[] | null;
  selectedRegex: RegexForm | null;
  regexes: (RegexForm | BankRegex)[];
  selectedBank: AdminBank | null;
}>;

const initialState: AdminBanksState = {
  banks: null,
  selectedRegex: null,
  regexes: [],
  selectedBank: null,
  loading: false,
  error: null,
};

const adminBanksSlice = createSlice({
  name: "adminBanks",
  initialState,
  reducers: {
    selectRegex: (
      state,
      action: PayloadAction<BankRegex | RegexForm | null | undefined>
    ) => {
      state.selectedRegex = action.payload ?? null;
    },
    updateRegexes: (state, action: PayloadAction<RegexForm>) => {
      const regexes = state.regexes;
      if (action.payload.id == null) {
        regexes.push(action.payload);
      } else {
        const foundRegex = regexes.find(
          (regex) => regex.id == action.payload.id
        );
        if (foundRegex != null) {
          foundRegex.description = action.payload.description;
          foundRegex.method = action.payload.method;
          foundRegex.regex = action.payload.regex;
          foundRegex.type = action.payload.type;
        } else {
          regexes.push(action.payload);
        }
      }
      state.regexes = [...regexes];
    },
    removeRegex: (state, action: PayloadAction<string>) => {
      state.regexes = state.regexes.filter(
        (regex) => regex.id !== action.payload
      );
    },
    setRegexes: (state, action: PayloadAction<BankRegex[]>) => {
      state.regexes = action.payload;
    },
    setSelectedBank: (state, action: PayloadAction<AdminBank | null>) => {
      state.selectedBank = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAdminBanks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAdminBanks.fulfilled, (state, action) => {
        state.loading = false;
        state.banks = action.payload.map((raw) => ({
          ...raw,
          push_notification_packages: JSON.stringify(
            raw.push_notification_packages
          ),
        }));
      })
      .addCase(getAdminBanks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createAdminBank.pending, (state) => {
        state.loading = true;
      })
      .addCase(createAdminBank.fulfilled, (state, action) => {
        state.loading = false;
        console.log(action.payload);
      })
      .addCase(createAdminBank.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(updateAdminBank.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAdminBank.fulfilled, (state, action) => {
        state.loading = false;
        console.log(action.payload);
      })
      .addCase(updateAdminBank.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(deleteAdminBank.pending, (state, action) => {
        state.loading = true;
        console.log(action.payload);
      })
      .addCase(deleteAdminBank.fulfilled, (state, action) => {
        state.loading = false;
        console.log(action.payload);
      })
      .addCase(deleteAdminBank.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  selectRegex,
  updateRegexes,
  setRegexes,
  setSelectedBank,
  removeRegex,
} = adminBanksSlice.actions;
export default adminBanksSlice.reducer;
